import type { MapKit, MapKitWindow } from './mapkit'

// Async MapKit JS loader for the RouteDetails walk-route map. Mirrors
// packages/station-details/src/rows/loadMapKit.ts (kept local so the
// RouteDetails map is self-contained + carries the Directions-capable
// `MapKit` type). Loads Apple's CDN script once and resolves the global
// `window.mapkit`.

const MAPKIT_JS_URL = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js'

let pending: Promise<MapKit> | null = null

function mapkitGlobal(): MapKit | undefined {
  return (window as unknown as MapKitWindow).mapkit
}

export function loadMapKit(): Promise<MapKit> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('mapkit: window is not available'))
  }
  const existingGlobal = mapkitGlobal()
  if (existingGlobal) {
    return Promise.resolve(existingGlobal)
  }
  if (pending) {
    return pending
  }
  pending = new Promise<MapKit>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${MAPKIT_JS_URL}"]`)
    const script = existing ?? document.createElement('script')
    const onLoad = (): void => {
      const loaded = mapkitGlobal()
      if (loaded) {
        resolve(loaded)
      } else {
        reject(new Error('mapkit: script loaded but window.mapkit is undefined'))
      }
    }
    const onError = (): void => {
      pending = null
      reject(new Error('mapkit: failed to load script'))
    }
    script.addEventListener('load', onLoad, { once: true })
    script.addEventListener('error', onError, { once: true })
    if (!existing) {
      script.src = MAPKIT_JS_URL
      script.crossOrigin = 'anonymous'
      script.async = true
      document.head.appendChild(script)
    }
  })
  return pending
}

export function __resetLoadMapKitForTests(): void {
  pending = null
}
